import { Injectable } from '@angular/core';
import { Camera, ICamera } from '../models/camera.model';
import { HttpClient } from '@angular/common/http';
import { AuthService } from './auth.service';
import { ToastService } from './toast.service';
import { UtilService } from './util.service';
import { environment } from '../../environments/environment';
import { firstValueFrom, map } from 'rxjs';
import { IServerResponse, ServerResponse } from '../models/server-response.model';
import { SpinnerService } from './spinner.service';

@Injectable({
  providedIn: 'root'
})
export class CameraService {

  public cameras: Camera[] = [];
  public loading = false;

  constructor(
    private http: HttpClient,
    private authService: AuthService,
    public spinnerService: SpinnerService,
    private toastService: ToastService,
    private utilService: UtilService
  ) {}

  /* -------------------------------------------------------------------------- */
  /*                                ANCHOR create                               */
  /* -------------------------------------------------------------------------- */
  // region create
  async create(
    neighborhoodId: string,
    camera: ICamera
  ): Promise<boolean> {
    let flag = true;

    this.spinnerService.loading = true;
    this.spinnerService.message = 'Registrando cámara...';

    try {
      const urlApi = `${environment.urlApi}/neighborhoods/${neighborhoodId}/cameras`;

      const token = await this.authService.getToken();
      const headers = this.utilService.headers(token);

      const response = await firstValueFrom(
        this.http.post<IServerResponse>(
          urlApi,
          camera,
          { headers }
        ).pipe(
          map(res => new ServerResponse(res))
        )
      );

      if (response.statusCode === 201) {
        flag = true;

        this.toastService.show({
          body: response.message,
          classname: 'bg-success text-white'
        });

        this.getAll(neighborhoodId);
      }

    } catch (error) {
      console.log('Error:', error);

      this.toastService.show({
        header: 'Error al crear cámara',
        body: `${error}`,
        classname: 'bg-danger text-white'
      });

    } finally {

      this.spinnerService.loading = false;
      this.spinnerService.message = '';

    }

    return flag;
  }

  /* -------------------------------------------------------------------------- */
  /*                                ANCHOR delete                               */
  /* -------------------------------------------------------------------------- */
  // region delete
  async delete(
    camera: Camera
  ): Promise<boolean> {
    let flag = true;

    this.spinnerService.loading = true;
    this.spinnerService.message = 'Eliminando cámara...';

    try {
      const urlApi = `${environment.urlApi}/neighborhoods/${camera.neighborhoodId}/cameras/${camera.cameraId}`;

      const token = await this.authService.getToken();
      const headers = this.utilService.headers(token);

      const response = await firstValueFrom(
        this.http.delete<IServerResponse>(
          urlApi,
          { headers }
        ).pipe(
          map(res => new ServerResponse(res))
        )
      );

      if (response.statusCode === 200) {
        flag = true;

        this.toastService.show({
          body: response.message,
          classname: 'bg-success text-white'
        });

        this.getAll(camera.neighborhoodId);
      }

    } catch (error) {
      console.log('Error:', error);

      this.toastService.show({
        header: 'Error al eliminar cámara',
        body: `${error}`,
        classname: 'bg-danger text-white'
      });

    } finally {
      this.spinnerService.loading = false;
      this.spinnerService.message = '';
    }

    return flag;
  }



  /* -------------------------------------------------------------------------- */
  /*                                ANCHOR getAll                               */
  /* -------------------------------------------------------------------------- */
  // region getAll
  async getAll(
    neighborhoodId: string
  ): Promise<Camera[]> {
    this.loading = true;

    try {
      const urlApi = `${environment.urlApi}/neighborhoods/${neighborhoodId}/cameras`;

      const token = await this.authService.getToken();
      const headers = this.utilService.headers(token);

      const response = await firstValueFrom(
        this.http.get<IServerResponse<{ cameras: ICamera[] }>>(
          urlApi, 
          { headers }
        ).pipe(
          map(res => new ServerResponse<{ cameras: ICamera[] }>(res))
        )
      );

      if (response.statusCode === 200) {
        this.cameras = (response.data?.cameras || []).map(
          e => new Camera(e)
        );
      }

    } catch (error) {
      console.log('Error:', error);

      this.toastService.show({
        header: 'Error al cargar cámaras',
        body: `${error}`,
        classname: 'bg-danger text-white'
      });

    } finally {
      this.loading = false;
    }

    return this.cameras;
  }

  /* -------------------------------------------------------------------------- */
  /*                              ANCHOR screenshot                             */
  /* -------------------------------------------------------------------------- */
  // region screenshot
  async screenshot(
    camera: Camera
  ): Promise<string | null> {
    this.spinnerService.loading = true;
    this.spinnerService.message = 'Obteniendo captura de pantalla...';

    let imageBase64 = null;

    const data = {
      ip: camera.ip,
      username: camera.username,
      password: camera.password,
      params: camera.params
    }

    let urlHost = 'http://localhost:8000';

    if(
      camera.hostIp != "" &&
      camera.hostPort > 0
    ) {
      urlHost = `http://${camera.hostIp}:${camera.hostPort}`;
    }

    try {
      const urlApi = `${urlHost}/api/v1/cameras/screenshot`;

      const response = await firstValueFrom(
        this.http.post<IServerResponse<{ image: string }>>(
          urlApi,
          JSON.stringify(data),
          { 
            headers: {
              'Content-Type': 'application/json'
            }
          }
        ).pipe(
          map(res => new ServerResponse<{ image: string }>(res))
        )
      );

      console.log('response:', response);
      

      if (response.statusCode === 200) {        

        imageBase64 = response.data?.image || '';
      } else {

        this.toastService.show({
          header: 'Error al obtener captura de pantalla',
          body: response.message,
          classname: 'bg-danger text-white'
        });

      }

    } catch (error) {
      console.log('Error:', error);

      this.toastService.show({
        header: 'Error al obtener captura de pantalla',
        body: `${error}`,
        classname: 'bg-danger text-white'
      });

    } finally {
      this.spinnerService.loading = false;
      this.spinnerService.message = '';
    }

    return imageBase64;
  }

  /* -------------------------------------------------------------------------- */
  /*                                ANCHOR update                               */
  /* -------------------------------------------------------------------------- */
  // region update
  async update(
    camera: Camera
  ): Promise<boolean> {
    let flag = true;

    this.spinnerService.loading = true;
    this.spinnerService.message = 'Actualizando cámara...';

    try {
      const urlApi = `${environment.urlApi}/neighborhoods/${camera.neighborhoodId}/cameras/${camera.cameraId}`;

      const token = await this.authService.getToken();
      const headers = this.utilService.headers(token);

      const response = await firstValueFrom(
        this.http.put<IServerResponse>(
          urlApi,
          camera,
          { headers }
        ).pipe(
          map(res => new ServerResponse(res))
        )
      );

      if (response.statusCode === 200) {
        flag = true;

        this.toastService.show({
          body: response.message,
          classname: 'bg-success text-white'
        });

        this.getAll(camera.neighborhoodId);
      }

    } catch (error) {
      console.log('Error:', error);

      this.toastService.show({
        header: 'Error al actualizar cámara',
        body: `${error}`,
        classname: 'bg-danger text-white'
      });

    } finally {
      this.spinnerService.loading = false;
      this.spinnerService.message = '';
    }

    return flag;
  }

  
}
